import type { PermissionProps } from "@qendo/permissions";
import {
    type OtherableTrackableList,
    type OtherableTrackableStore,
    type OtherableTrackableType,
    type TrackableData,
    type TrackableReference,
    TrackableType,
} from "@qendo/trackables";
import type { MultiListData } from "@qendo/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { useApi } from "~/providers/ApiProvider";
import { useSystemOptions } from "~/providers/SystemOptionsProvider";
import { filled } from "~/util";

import { journalPermissionSection } from "~/util/qendo/permissions";

const EmptyOtherableTrackables: OtherableTrackableStore = {
    [TrackableType.activity]: [],
    [TrackableType.symptom]: [],
};

declare interface TrackablePayload {
    type: TrackableType;
    title: string;
}
declare interface StoreTrackableResponse {
    created: boolean;
    type: TrackableType;
    trackable: TrackableData<TrackableType>;
}

declare type TrackableState = {
    listTrackables<T extends TrackableType>(type: T): TrackableData<T>[];
    findTrackable(id?: TrackableReference | null): TrackableData | null;
    canMakeTrackable(type: TrackableType): boolean;
    storeTrackable<T extends OtherableTrackableType>(type: T, title: string): Promise<string | null>;
    refreshTrackables(): Promise<void>;
};

export const TrackableContext = createContext<TrackableState>({
    listTrackables: (_type) => [],
    findTrackable: (_id) => null,
    canMakeTrackable: (_type) => false,
    storeTrackable: async (_type, _title) => null,
    refreshTrackables: async () => {
        return;
    },
});

export function useTrackable() {
    const value = useContext(TrackableContext);
    if (!value) {
        throw new Error("useTrackable must be wrapped in a <TrackableProvider />");
    }
    return value;
}

export type TrackableProviderProps = {
    profile_id: string | null;
    children: ReactNode;
} & PermissionProps;

export function TrackableProvider({ profile_id, can, children }: TrackableProviderProps) {
    const queryClient = useQueryClient();
    const { client } = useApi();
    const { trackables: systemTrackables, refreshTrackables: systemRefreshTrackables } = useSystemOptions();

    const queryKey = ["trackables", profile_id];

    const profileQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (profile_id) {
                return await client()
                    .get(`profiles/${profile_id}/trackables`)
                    .then((response: AxiosResponse<MultiListData<TrackableType, OtherableTrackableList>>) => {
                        return {
                            ...EmptyOtherableTrackables,
                            ...response.data.data,
                        };
                    })
                    .catch(() => EmptyOtherableTrackables);
            }
            return EmptyOtherableTrackables;
        },
        staleTime: 1000 * 60 * 60, // 1 hour,
        placeholderData: EmptyOtherableTrackables,
        refetchOnMount: true,
    });
    const profileTrackables = useMemo(
        () => (profile_id ? (profileQuery.data ?? EmptyOtherableTrackables) : EmptyOtherableTrackables),
        [profile_id, profileQuery],
    );

    const listTrackables = useCallback(
        <T extends TrackableType>(type: T): TrackableData<T>[] => [
            // @ts-expect-error
            ...(profileTrackables[type] ?? []),
            ...systemTrackables[type],
        ],
        [profileTrackables, systemTrackables],
    );

    const allTrackables = useMemo(
        () => [
            ...listTrackables(TrackableType.activity),
            ...listTrackables(TrackableType.symptom),
            ...listTrackables(TrackableType.journal),
        ],
        [listTrackables],
    );

    const findTrackable = (id?: TrackableReference | null): TrackableData | null => {
        return (filled(id) && allTrackables.find((t) => t.id === id || t.legacy_id === id)) || null;
    };

    const canMakeTrackable = (type: TrackableType) => {
        return (
            [TrackableType.activity, TrackableType.symptom].includes(type) &&
            can("update", journalPermissionSection(type))
        );
    };

    const storeTrackableMutation = useMutation({
        mutationFn: async ({ type, title }: TrackablePayload) => {
            return await client()
                .post(`profiles/${profile_id}/trackables/${type}`, { title })
                .then((response: AxiosResponse<TrackableData<TrackableType>>): StoreTrackableResponse => {
                    return {
                        created: response.status === 201,
                        type,
                        trackable: response.data,
                    };
                });
        },
        onSuccess: ({ created, type, trackable }: StoreTrackableResponse) => {
            if (!created) {
                return;
            }

            queryClient.setQueryData(queryKey, (cache?: OtherableTrackableStore) => {
                const current = cache ?? EmptyOtherableTrackables;

                return {
                    ...current,
                    [type]: [trackable, ...current[type as OtherableTrackableType]],
                };
            });
        },
    });

    const storeTrackable = async <T extends OtherableTrackableType>(type: T, title: string) => {
        if (!canMakeTrackable(type)) {
            return null;
        }

        return await storeTrackableMutation.mutateAsync({ type, title }).then(({ trackable }) => trackable.id);
    };

    const refreshTrackables = async () => {
        await Promise.allSettled([systemRefreshTrackables(), queryClient.invalidateQueries({ queryKey: queryKey })]);
    };

    const value = {
        listTrackables,
        findTrackable,
        canMakeTrackable,
        storeTrackable,
        refreshTrackables,
    };

    return <TrackableContext.Provider value={value}>{children}</TrackableContext.Provider>;
}
